import React from 'react';
import { FieldLabel } from './FieldLabel';
import { FieldError } from './FieldError';

export const FieldWrapper = ({ field, error, theme, children }) => {
  // Column span mapping for Tailwind classes
  const getColumnSpanClass = (span) => {
    const spanClasses = {
      '1': 'col-span-1',
      '2': 'col-span-2',
      '3': 'col-span-3',
      '4': 'col-span-4',
      '5': 'col-span-5',
      '6': 'col-span-6',
      '7': 'col-span-7',
      '8': 'col-span-8',
      '9': 'col-span-9',
      '10': 'col-span-10',
      '11': 'col-span-11',
      '12': 'col-span-12',
      'full': 'col-span-full',
      'auto': 'col-auto'
    };
    return spanClasses[span] || '';
  };

  // Column start mapping for Tailwind classes
  const getColumnStartClass = (start) => {
    const startClasses = {
      '1': 'col-start-1',
      '2': 'col-start-2',
      '3': 'col-start-3',
      '4': 'col-start-4',
      '5': 'col-start-5',
      '6': 'col-start-6',
      '7': 'col-start-7',
      '8': 'col-start-8',
      '9': 'col-start-9',
      '10': 'col-start-10',
      '11': 'col-start-11',
      '12': 'col-start-12',
      'auto': 'col-start-auto'
    };
    return startClasses[start] || '';
  };

  // Get responsive column classes
  const getResponsiveColumnClasses = () => {
    let classes = [];
    
    // Handle responsive column spans
    if (field.colSpan) {
      if (typeof field.colSpan === 'object') {
        // Responsive spans: { sm: 6, md: 4, lg: 3 }
        Object.entries(field.colSpan).forEach(([breakpoint, span]) => {
          if (breakpoint === 'default') {
            classes.push(getColumnSpanClass(span));
          } else {
            classes.push(`${breakpoint}:${getColumnSpanClass(span).replace('col-span-', 'col-span-')}`);
          }
        });
      } else {
        // Single span value
        classes.push(getColumnSpanClass(field.colSpan));
      }
    }
    
    // Handle column start
    if (field.colStart) {
      if (typeof field.colStart === 'object') {
        Object.entries(field.colStart).forEach(([breakpoint, start]) => {
          if (breakpoint === 'default') {
            classes.push(getColumnStartClass(start));
          } else {
            classes.push(`${breakpoint}:${getColumnStartClass(start).replace('col-start-', 'col-start-')}`);
          }
        });
      } else {
        classes.push(getColumnStartClass(field.colStart));
      }
    }
    
    return classes.join(' ');
  };

  // Build inline styles for non-Tailwind usage
  const getInlineStyles = () => {
    let styles = {};
    
    if (field.colSpan) {
      const span = typeof field.colSpan === 'object' ? field.colSpan.default || field.colSpan : field.colSpan;
      if (span === 'full') {
        styles.gridColumn = '1 / -1';
      } else if (span) {
        styles.gridColumn = `span ${span}`;
      }
    }
    
    if (field.colStart) {
      const start = typeof field.colStart === 'object' ? field.colStart.default || field.colStart : field.colStart;
      if (start) {
        styles.gridColumnStart = start;
      }
    }
    
    // Add any custom styles from the field
    if (field.style) {
      styles = { ...styles, ...field.style };
    }
    
    return styles;
  };

  // Determine wrapper classes
  const wrapperClasses = [
    theme.fieldWrapper,
    getResponsiveColumnClasses(),
    field.className // Allow custom classes on the field
  ].filter(Boolean).join(' ');

  // Determine if we should use inline styles
  const useInlineStyles = !theme.useTailwind || field.useInlineStyles;
  const inlineStyles = useInlineStyles ? getInlineStyles() : undefined;

  return (
    <div 
      className={wrapperClasses}
      style={inlineStyles}
      data-field-id={field.id}
    >
      <FieldLabel field={field} theme={theme} />
      {children}
      {field.hint && !error && (
        <p className={theme.hint}>{field.hint}</p>
      )}
      <FieldError error={error} theme={theme} />
    </div>
  );
};